import React, { useContext, useEffect, useState } from 'react';
import ProjectContainer from '../../components/containers/ProjectContainer';
import { getAppConfiguration, setAppConfiguration } from '../../api/appConfiguration';
import { Components } from './styled';
import TabComponent from './components/TabComponent';
import { Section, Tab } from '../../api/appConfiguration/types';
import { ActivityIndicatorContext } from '../../providers/ActivityIndicatorProvider/context';
import { ToastContext } from '../../providers/ToastProvider/context';
import { ProjectErrors } from '../../utils/errors/types';
import { getProjectErrorForCode } from '../../utils/errors';
import { SessionContext } from '../../providers/SessionProvider/context';
import { canDeleteOrChangeSection } from './utils';

const AppConfiguration = () => {
  const { setIsLoading } = useContext(ActivityIndicatorContext);
  const { showSuccessToast, showErrorToast } = useContext(ToastContext);
  const { currentProfile } = useContext(SessionContext);

  const [tabs, setTabs] = useState<Tab[]>([]);

  useEffect(() => {
    setIsLoading(true);
    getAppConfiguration().then((res) => {
      setTabs(res);
      setIsLoading(false);
    });
  }, []);

  const refreshTrigger = async () => {
    setIsLoading(true);
    getAppConfiguration().then((res) => {
      setTabs(res);
      setIsLoading(false);
    });
  };

  const _moveSection = (itemKey: string, direction: 'up' | 'down') => {
    setTabs((prevTabs) => {
      let didSectionMove = false;
      const newTabs = prevTabs.map((tab) => {
        const sectionIndex = tab.sections.findIndex((section) => section.key === itemKey);
        if (sectionIndex === -1) {
          return tab;
        }

        const newSections = [...tab.sections];
        if (direction === 'up' && sectionIndex > 0) {
          [newSections[sectionIndex], newSections[sectionIndex - 1]] = [
            newSections[sectionIndex - 1],
            newSections[sectionIndex],
          ];

          didSectionMove = true;
        } else if (direction === 'down' && sectionIndex < newSections.length - 1) {
          [newSections[sectionIndex], newSections[sectionIndex + 1]] = [
            newSections[sectionIndex + 1],
            newSections[sectionIndex],
          ];

          didSectionMove = true;
        }

        return {
          ...tab,
          sections: newSections,
        };
      });

      setAppConfiguration(newTabs).then((res) => {
        if (!didSectionMove) {
          return;
        }

        if (!res) {
          showSuccessToast('Moved section successfully');
        } else {
          showErrorToast(getProjectErrorForCode(ProjectErrors.failedToMoveSection));
        }
      });

      return newTabs;
    });
  };

  const onEditSection = (itemKey: string, section: Section) => {
    console.log('onEditSection', itemKey, section);
  };

  const onMoveUpSection = (itemKey: string) => {
    _moveSection(itemKey, 'up');
  };

  const onMoveDownSection = (itemKey: string) => {
    _moveSection(itemKey, 'down');
  };

  const onDeleteSection = (itemKey: string) => {
    const allSections = tabs?.reduce((res, item) => res?.concat(item?.sections), [] as Section[]);
    const section = allSections?.find((item) => item?.key === itemKey);
    if (
      section &&
      currentProfile &&
      (currentProfile?.isSuperAdmin || canDeleteOrChangeSection({ appConfiguration: tabs, section }))
    ) {
      setTabs((prevTabs) => {
        const newTabs = prevTabs.map((tab) => {
          const sectionIndex = tab.sections.findIndex((section) => section.key === itemKey);
          if (sectionIndex === -1) {
            return tab;
          }

          const newSections = [...tab.sections];
          newSections.splice(sectionIndex, 1);

          return {
            ...tab,
            sections: newSections,
          };
        });

        setAppConfiguration(newTabs).then((res) => {
          if (!res) {
            showSuccessToast('Deleted section successfully');
          } else {
            showErrorToast(getProjectErrorForCode(ProjectErrors.failedToDeleteSection));
          }
        });

        return newTabs;
      });
    } else {
      showErrorToast(getProjectErrorForCode(ProjectErrors.cannotChangeSection));
    }
  };

  // const allSections = tabs?.reduce((res, item) => res?.concat(item?.sections), [] as Section[]);
  return (
    <ProjectContainer title={'App Configuration'}>
      <Components.Container>
        <Components.TabContainer>
          {tabs.map((tab) => (
            <TabComponent
              key={tab.tab}
              title={tab.tab.toUpperCase()}
              tabType={tab.tab}
              sections={tab.sections}
              appConfiguration={tabs}
              onEditSection={onEditSection}
              onMoveUpSection={onMoveUpSection}
              onMoveDownSection={onMoveDownSection}
              onDeleteSection={onDeleteSection}
              refreshCallback={refreshTrigger}
              tabs={tabs}
              setTabs={setTabs}
            />
          ))}
        </Components.TabContainer>
      </Components.Container>
    </ProjectContainer>
  );
};

export default AppConfiguration;
